import { COULEURS, CHARTE } from '../theme/charte';
import { DocumentClient, ETATS_A_REGLER, formatMontant } from './documents';
import { Mission } from './missions';
import { jourCourt, libelleFacture } from './situation';

/* Fil de la mission (maquette Mission, onglet Suivi). Aucune route ne donne
   au client le journal de la mission : le fil se construit à partir de ce
   que le portail lit déjà — statuts et dates de la mission, documents émis
   (admin-service v2), trace serveur de la signature. Ce qui attend le
   client vient en tête (« Aujourd'hui »), puis le reste du plus récent au
   plus ancien. */

export interface EvenementFil {
  cle: string;
  quand: string;
  /** Pour le tri ; vide pour ce qui attend le client. */
  date: string;
  quoi: string;
  detail?: string;
  couleur: string;
  /** Ce qui attend le client : en tête, libellé « Aujourd'hui » en orange. */
  enAttente?: boolean;
}

const ttc = (m?: number | null) => `${formatMontant(m)} TTC`;

export function filMission(m: Mission, documents: DocumentClient[], nbSpotters?: number | null): EvenementFil[] {
  const ev: EvenementFil[] = [];
  const signe = m.signatureDevis;

  for (const d of documents) {
    if (d.type === 'FACTURE' && ETATS_A_REGLER.includes(d.etatPaiement || '')) {
      const echeance = d.dateEcheance ? ` avant le ${jourCourt(d.dateEcheance)}` : '';
      ev.push({
        cle: `a-regler-${d.id}`, quand: "Aujourd'hui", date: '', enAttente: true, couleur: COULEURS.action,
        quoi: `${libelleFacture(d)} à régler${echeance}`,
        detail: `Facture ${d.numero} · reste dû ${formatMontant(d.resteDuTtc ?? d.montantTtc)}`,
      });
    }
  }
  const devisEnAttente = documents
    .filter(d => d.type === 'DEVIS' && (!signe || signe.devisNumero !== d.numero))
    .sort((a, b) => (b.emisLe || '').localeCompare(a.emisLe || ''))[0];
  const aSigner = devisEnAttente && (m.statutPaiement == null || m.statutPaiement === '' || m.statutPaiement === 'devis_envoye');
  if (aSigner && devisEnAttente) {
    ev.push({
      cle: `a-signer-${devisEnAttente.id}`, quand: "Aujourd'hui", date: '', enAttente: true, couleur: COULEURS.action,
      quoi: 'Devis à signer', detail: `${devisEnAttente.numero} · ${ttc(devisEnAttente.montantTtc)}`,
    });
  }

  if (m.statut === 'terminee' && (m.updatedAt || m.dateFin)) {
    const date = m.updatedAt || m.dateFin || '';
    ev.push({ cle: 'livree', quand: jourCourt(date), date, couleur: COULEURS.succes,
      quoi: 'Mission livrée', detail: `${(m.collectesRealisees ?? 0).toLocaleString('fr-FR')} collectes` });
  }
  if (signe?.signeLe) {
    ev.push({ cle: 'signature', quand: jourCourt(signe.signeLe), date: signe.signeLe, couleur: COULEURS.succes,
      quoi: `Devis signé · ${signe.devisNumero}`,
      detail: [signe.montantTtc != null ? ttc(signe.montantTtc) : null, signe.signataire ? `par ${signe.signataire}` : null]
        .filter(Boolean).join(' · ') || undefined });
  }
  for (const d of documents) {
    if (!d.emisLe) continue;
    if (d.type === 'CONTRAT') {
      ev.push({ cle: `doc-${d.id}`, quand: jourCourt(d.emisLe), date: d.emisLe, couleur: COULEURS.succes,
        quoi: `Contrat émis · ${d.numero}`, detail: ttc(d.montantTtc) });
    } else if (d.type === 'DEVIS') {
      ev.push({ cle: `doc-${d.id}`, quand: jourCourt(d.emisLe), date: d.emisLe, couleur: CHARTE.ocreClair,
        quoi: `Devis émis · ${d.numero}`, detail: ttc(d.montantTtc) });
    } else if (d.type === 'FACTURE') {
      const reglee = d.etatPaiement === 'PAYEE';
      ev.push({ cle: `doc-${d.id}`, quand: jourCourt(d.emisLe), date: d.emisLe, couleur: reglee ? COULEURS.succes : COULEURS.info,
        quoi: `${d.natureFacture === 'ACOMPTE' ? "Facture d'acompte" : d.natureFacture === 'SOLDE' ? 'Facture de solde' : 'Facture'} émise · ${d.numero}`, detail: `${ttc(d.montantTtc)}${reglee ? ' · réglée' : ''}` });
    } else if (d.type === 'AVOIR') {
      ev.push({ cle: `doc-${d.id}`, quand: jourCourt(d.emisLe), date: d.emisLe, couleur: COULEURS.neutre,
        quoi: `Avoir émis · ${d.numero}`, detail: d.documentOrigineNumero ? `sur ${d.documentOrigineNumero}` : undefined });
    }
  }
  if (m.dateDebut && (m.statut === 'publiee' || m.statut === 'active' || m.statut === 'terminee')) {
    ev.push({ cle: 'lancement', quand: jourCourt(m.dateDebut), date: m.dateDebut, couleur: COULEURS.succes,
      quoi: nbSpotters ? `Collecte lancée · ${nbSpotters} Spotter${nbSpotters > 1 ? 's' : ''} mobilisé${nbSpotters > 1 ? 's' : ''}` : 'Collecte lancée' });
  }
  if (m.createdAt) {
    ev.push({ cle: 'demande', quand: jourCourt(m.createdAt), date: m.createdAt, couleur: COULEURS.neutre,
      quoi: 'Demande transmise à Tribe' });
  }

  return [
    ...ev.filter(e => e.enAttente),
    ...ev.filter(e => !e.enAttente).sort((a, b) => b.date.localeCompare(a.date)),
  ];
}
